import React, { useEffect, useState } from "react";
import { Loader2, Save, AlertTriangle, Trash2, KeyRound, Mail, UserPlus, ShieldCheck } from "lucide-react";
import api, { apiError, rupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function PengaturanTab() {
  const { logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [changingPwd, setChangingPwd] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", username: "", password: "", email: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);

  const loadAdmins = async () => {
    try { setAdmins((await api.get("/admins")).data); } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    loadAdmins();
  }, []);

  const changePassword = async () => {
    if (!pwd.current_password || !pwd.new_password) return toast.error("Lengkapi password");
    setChangingPwd(true);
    try {
      await api.put("/admin/password", pwd);
      toast.success("Password berhasil diubah");
      setPwd({ current_password: "", new_password: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setChangingPwd(false); }
  };

  const addAdmin = async () => {
    if (!newAdmin.name || !newAdmin.username || !newAdmin.password) return toast.error("Lengkapi data admin");
    setAddingAdmin(true);
    try {
      await api.post("/admins", newAdmin);
      toast.success("Admin baru ditambahkan");
      setNewAdmin({ name: "", username: "", password: "", email: "" });
      await loadAdmins();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAddingAdmin(false); }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Hapus admin "${a.name}" (@${a.username})?`)) return;
    try { await api.delete(`/admins/${a.id}`); toast.success("Admin dihapus"); await loadAdmins(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        late_tolerance_minutes: Number(settings.late_tolerance_minutes),
        late_deduction_per_hour: Number(settings.late_deduction_per_hour),
        recovery_email: settings.recovery_email || "",
        auto_close_default_minutes: Number(settings.auto_close_default_minutes || 180),
        unresolved_review_days: Number(settings.unresolved_review_days || 3),
        unresolved_default_penalty: Number(settings.unresolved_default_penalty || 0),
        forgot_checkout_base_discipline_penalty: Number(settings.forgot_checkout_base_discipline_penalty || 1),
        forgot_checkout_repeat_discipline_penalty: Number(settings.forgot_checkout_repeat_discipline_penalty || 3),
      });
      setSettings(data);
      toast.success("Pengaturan disimpan");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setResetting(true);
    try {
      await api.post("/admin/reset", { confirm });
      toast.success("Database berhasil direset. Silakan login ulang.");
      setTimeout(() => logout(), 1500);
    } catch (e) { toast.error(apiError(e)); setResetting(false); }
  };

  if (!settings) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <h2 className="text-xl font-bold text-slate-800">Pengaturan</h2>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="space-y-1.5">
          <Label>Toleransi Keterlambatan (menit)</Label>
          <Input type="number" data-testid="tolerance-input" value={settings.late_tolerance_minutes}
            onChange={(e) => setSettings({ ...settings, late_tolerance_minutes: e.target.value })} />
          <p className="text-xs text-slate-400">Check-In dalam batas toleransi dihitung Tepat Waktu.</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Auto-Close Default (menit)</Label>
            <Input type="number" min="0" data-testid="default-auto-close-input" value={settings.auto_close_default_minutes || 180}
              onChange={(e) => setSettings({ ...settings, auto_close_default_minutes: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Batas Review Unresolved (hari)</Label>
            <Input type="number" min="1" data-testid="unresolved-review-days-input" value={settings.unresolved_review_days || 3}
              onChange={(e) => setSettings({ ...settings, unresolved_review_days: e.target.value })} /></div>
        </div>
        <div className="space-y-1.5"><Label>Potongan Default Unresolved (Rp)</Label>
          <Input type="number" min="0" data-testid="unresolved-default-penalty-input" value={settings.unresolved_default_penalty || 0}
            onChange={(e) => setSettings({ ...settings, unresolved_default_penalty: e.target.value })} /></div>
        <div className="space-y-1.5">
          <Label>Potongan Keterlambatan (Rupiah per jam)</Label>
          <Input type="number" data-testid="deduction-input" value={settings.late_deduction_per_hour}
            onChange={(e) => setSettings({ ...settings, late_deduction_per_hour: e.target.value })} />
          <p className="text-xs text-slate-400">Potongan = (menit terlambat / 60) × {rupiah(settings.late_deduction_per_hour)}.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Email Pemulihan Password (Forgot Password)</Label>
          <Input type="email" data-testid="recovery-email-input" value={settings.recovery_email || ""}
            onChange={(e) => setSettings({ ...settings, recovery_email: e.target.value })} placeholder="email@perusahaan.com" />
          <p className="text-xs text-slate-400">Email tujuan untuk pemulihan/lupa password akun admin.</p>
        </div>
        <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-[#3368A0] hover:bg-[#284f7a]">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Simpan Pengaturan</>}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#C8DFDB] p-6 space-y-4">
        <div className="flex items-center gap-2 text-[#3368A0]">
          <KeyRound className="h-5 w-5" />
          <h3 className="font-bold">Ganti Password Admin</h3>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Password Saat Ini</Label>
            <Input type="password" data-testid="current-password-input" value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Password Baru</Label>
            <Input type="password" data-testid="new-password-input" value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} placeholder="Minimal 6 karakter" />
          </div>
        </div>
        <Button data-testid="change-password-button" onClick={changePassword} disabled={changingPwd} className="bg-[#3368A0] hover:bg-[#284f7a]">
          {changingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" /> Ubah Password</>}
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#C8DFDB] p-6 space-y-4">
        <div className="flex items-center gap-2 text-[#3368A0]">
          <ShieldCheck className="h-5 w-5" />
          <h3 className="font-bold">Akun Admin</h3>
        </div>
        <div className="space-y-2" data-testid="admin-list">
          {admins.map((a) => (
            <div key={a.id} className="flex items-center justify-between bg-[#F2EFE7] rounded-xl px-4 py-2.5">
              <div>
                <p className="font-medium text-slate-700 text-sm">{a.name} <span className="text-slate-400">@{a.username}</span></p>
                {a.email && <p className="text-xs text-slate-400 flex items-center gap-1"><Mail className="h-3 w-3" /> {a.email}</p>}
              </div>
              <button onClick={() => removeAdmin(a)} data-testid={`delete-admin-${a.id}`} className="text-rose-500 hover:text-rose-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-slate-600 flex items-center gap-1.5"><UserPlus className="h-4 w-4" /> Tambah Admin Baru</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="new-admin-name-input" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder="Nama lengkap" />
            <Input data-testid="new-admin-username-input" value={newAdmin.username} onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })} placeholder="Username" />
            <Input type="text" data-testid="new-admin-password-input" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="Password" />
            <Input type="email" data-testid="new-admin-email-input" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder="Email (opsional)" />
          </div>
          <Button data-testid="add-admin-button" onClick={addAdmin} disabled={addingAdmin} className="bg-[#3368A0] hover:bg-[#284f7a]">
            {addingAdmin ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" /> Tambah Admin</>}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-rose-200 p-6 space-y-4">
        <div className="flex items-center gap-2 text-rose-600">
          <AlertTriangle className="h-5 w-5" />
          <h3 className="font-bold">Reset Database</h3>
        </div>
        <p className="text-sm text-slate-500">
          Tindakan ini akan menghapus SELURUH data secara permanen dan tidak dapat dibatalkan.
          Data awal (akun demo, outlet demo, 3 shift default) akan dibuat ulang. Ketik <b>RESET</b> untuk konfirmasi.
        </p>
        <Input data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Ketik RESET" className="border-rose-200" />
        <Button data-testid="reset-db-button" onClick={reset} disabled={confirm !== "RESET" || resetting} variant="destructive">
          {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4 mr-1.5" /> Reset Database</>}
        </Button>
      </div>
      </div>
    </div>
  );
}
